<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { getWeightRecord, saveWeightRecord } from '../services/db.js'
  import { validateWeightRecord } from '../utils/validation.js'

  export let mode = 'create' // 'create' | 'edit'
  export let recordId = null

  const dispatch = createEventDispatcher()

  let weight = ''
  let bodyFat = ''
  let date = ''
  let errors = {}
  let isSaving = false

  // Convert Date object to local datetime string for datetime-local input
  function toLocalDateTimeString(dateObj) {
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  onMount(async () => {
    // Set default date to now (in local timezone)
    const now = new Date()
    date = toLocalDateTimeString(now)

    // Load existing record if editing
    if (mode === 'edit' && recordId) {
      try {
        const record = await getWeightRecord(recordId)
        if (record) {
          weight = record.weight
          bodyFat = record.bodyFat || ''
          date = toLocalDateTimeString(new Date(record.date))
        }
      } catch (error) {
        console.error('Failed to load weight record:', error)
      }
    }
  })

  async function handleSubmit() {
    errors = {}

    const record = {
      weight: weight ? parseFloat(weight) : null,
      bodyFat: bodyFat ? parseFloat(bodyFat) : null,
      date: date ? new Date(date).toISOString() : null
    }

    // Validate
    const validation = validateWeightRecord(record)
    if (!validation.valid) {
      errors = validation.errors
      return
    }

    isSaving = true

    try {
      // Add ID if editing
      if (mode === 'edit' && recordId) {
        record.id = recordId
      }

      await saveWeightRecord(record)
      dispatch('save')
    } catch (error) {
      console.error('Failed to save weight record:', error)
      errors.general = '儲存失敗，請稍後再試'
    } finally {
      isSaving = false
    }
  }

  function handleCancel() {
    dispatch('close')
  }
</script>

<div class="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4">
  <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
    <!-- Header -->
    <div class="border-b border-gray-200 p-4">
      <h2 class="text-xl font-semibold text-gray-800">
        {mode === 'create' ? '新增體重記錄' : '編輯體重記錄'}
      </h2>
    </div>

    <!-- Form -->
    <form on:submit|preventDefault={handleSubmit} class="p-4 space-y-4">
      <!-- Weight Input -->
      <div>
        <label for="weight" class="block text-sm font-medium text-gray-700 mb-1">
          體重 (kg) <span class="text-red-500">*</span>
        </label>
        <input
          type="number"
          inputmode="decimal"
          id="weight"
          bind:value={weight}
          step="0.1"
          min="20"
          max="300"
          placeholder="70.5"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-red-500={errors.weight}
          required
        />
        {#if errors.weight}
          <p class="mt-1 text-sm text-red-600">{errors.weight}</p>
        {/if}
      </div>

      <!-- Body Fat Input -->
      <div>
        <label for="bodyFat" class="block text-sm font-medium text-gray-700 mb-1">
          體脂率 (%)
        </label>
        <input
          type="number"
          id="bodyFat"
          bind:value={bodyFat}
          step="0.1"
          min="0"
          max="100"
          placeholder="18.5"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-red-500={errors.bodyFat}
        />
        {#if errors.bodyFat}
          <p class="mt-1 text-sm text-red-600">{errors.bodyFat}</p>
        {/if}
      </div>

      <!-- Date Input -->
      <div>
        <label for="date" class="block text-sm font-medium text-gray-700 mb-1">
          記錄時間 <span class="text-red-500">*</span>
        </label>
        <input
          type="datetime-local"
          id="date"
          bind:value={date}
          max={toLocalDateTimeString(new Date())}
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-red-500={errors.date}
          required
        />
        {#if errors.date}
          <p class="mt-1 text-sm text-red-600">{errors.date}</p>
        {/if}
      </div>

      <!-- General Error -->
      {#if errors.general}
        <div class="bg-red-50 border border-red-200 rounded-md p-3">
          <p class="text-sm text-red-600">{errors.general}</p>
        </div>
      {/if}

      <!-- Buttons -->
      <div class="flex gap-3 pt-2">
        <button
          type="button"
          on:click={handleCancel}
          class="flex-1 btn btn-secondary"
          disabled={isSaving}
        >
          取消
        </button>
        <button
          type="submit"
          class="flex-1 btn btn-primary"
          disabled={isSaving}
        >
          {isSaving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  </div>
</div>
